'use client';

import { Switch } from '@ps/ui/atoms/switch';
import { cn } from '@ps/ui/utils';
import { Bell } from 'lucide-react';

import { useChannelNotificationSettings } from '../../hooks/useChannelNotificationSettings';
import type { CommunityChannelMember } from '../../types/CommunityChannelMember';

interface Props {
  channelId: string;
  /** The viewer's live membership doc — supplies the current toggle state. */
  member: CommunityChannelMember | null | undefined;
  /**
   * Show the "Creator Messages" checkbox (notify when the creator posts). Shown
   * to members, hidden for the owner (who IS the creator) — mirrors upstream.
   */
  includeCreatorMessages?: boolean;
}

/**
 * The channel-info "Notifications" block — a master mute switch plus the
 * independent "Creator Messages" (members only) and "Mentions in Channel"
 * checkboxes. Shared by the owner {@link ChannelInfoPopover} and the member
 * {@link ChannelInfoDialog}. Writes go through `useChannelNotificationSettings`;
 * the live state is read from the passed `member` doc. Both checkboxes disable
 * while muted (a muted channel sends nothing).
 */
export function ChannelNotificationsControl({
  channelId,
  member,
  includeCreatorMessages = false,
}: Props) {
  const { setMuted, setSubscribedToMentions, setSubscribedToUpdatesOfCreator } =
    useChannelNotificationSettings({ channelId });

  const muted = member?.muted ?? false;
  const mentionsOn = member?.subscribedToMentions ?? true;
  const creatorUpdatesOn = member?.subscribedToUpdatesOfCreator ?? true;

  return (
    <div className="flex flex-col gap-2 px-3 py-2.5">
      <div className="flex items-center justify-between gap-3">
        <div className="text-content-1 flex items-center gap-2 text-sm">
          <Bell className="h-4 w-4" aria-hidden /> Notifications
        </div>
        <Switch
          checked={!muted}
          onCheckedChange={next => {
            void setMuted(!next);
          }}
        />
      </div>
      <div className="bg-surface-2 flex items-center gap-2 rounded-lg p-1">
        {includeCreatorMessages ? (
          <NotificationCheckbox
            className="flex-1"
            label="Creator Messages"
            checked={creatorUpdatesOn}
            disabled={muted}
            onChange={checked => void setSubscribedToUpdatesOfCreator(checked)}
          />
        ) : null}
        <NotificationCheckbox
          className="flex-1"
          label="Mentions in Channel"
          checked={mentionsOn}
          disabled={muted}
          onChange={checked => void setSubscribedToMentions(checked)}
        />
      </div>
    </div>
  );
}

function NotificationCheckbox({
  className,
  label,
  checked,
  disabled,
  onChange,
}: {
  className?: string;
  label: string;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label
      className={cn(
        'flex items-center gap-2 rounded-md px-2 py-1.5 text-xs',
        disabled ? 'text-content-4' : 'text-content-2',
        className,
      )}
    >
      <input
        type="checkbox"
        className="accent-mint-green-300 h-4 w-4"
        checked={checked}
        disabled={disabled}
        onChange={event => onChange(event.target.checked)}
      />
      {label}
    </label>
  );
}
